/**
 * Make a dental chart safe to send. The server refuses any chart with an
 * invalid FDI tooth, a repeated tooth, an unknown condition or a mobility
 * grade outside I-III, and a refused visit save is not retried: online the
 * whole save fails, offline the queued update is marked failed. Every chart
 * therefore passes through here before it leaves the device.
 *
 * Findings built by the chart UI are already clean. The case this exists for
 * is a draft reopened from the "Dental Findings" text in Chief Complaint,
 * which the doctor can edit by hand ("Tooth 9", the same tooth twice).
 */
import { DENTAL_CONDITIONS, PERMANENT_TEETH, PRIMARY_TEETH, type ConditionKey } from "../constants/dental.constants";
import type { DentalFinding, PlanItem } from "../schemas/dental.schema";

const VALID_TEETH = new Set<number>([...PERMANENT_TEETH, ...PRIMARY_TEETH]);
const KNOWN = new Set<string>(DENTAL_CONDITIONS.map((c) => c.key));

/** Same limit as MAX_PLAN_ITEMS in the backend DentalChart schema. */
export const MAX_PLAN_ITEMS = 100;

export function sanitizeFindings(findings: readonly { tooth: number; conditions: readonly string[]; mobility_grade?: number | null }[]): DentalFinding[] {
  const byTooth = new Map<number, DentalFinding>();
  for (const f of findings) {
    if (!VALID_TEETH.has(f.tooth)) continue;
    const prev = byTooth.get(f.tooth);
    const conditions = [...(prev?.conditions ?? [])];
    for (const c of f.conditions) if (KNOWN.has(c) && !conditions.includes(c as ConditionKey)) conditions.push(c as ConditionKey);
    const m = f.mobility_grade;
    const mobility = m === 1 || m === 2 || m === 3 ? m : null;
    const mobility_grade = Math.max(prev?.mobility_grade ?? 0, mobility ?? 0) as 0 | 1 | 2 | 3;
    byTooth.set(f.tooth, { tooth: f.tooth, conditions, mobility_grade: mobility_grade || null });
  }
  return [...byTooth.values()]
    .filter((f) => f.conditions.length > 0 || f.mobility_grade)
    .sort((a, b) => a.tooth - b.tooth);
}

/**
 * Combine the stored chart with anything charted before it arrived (a late or
 * retried load). Findings are merged per tooth; plan items are kept by id,
 * stored first. On a normal reopen nothing is charted yet, so the result is
 * the stored chart as it was.
 */
export function mergeRestored(
  stored: { findings: DentalFinding[]; plan_items: PlanItem[] },
  session: { findings: DentalFinding[]; plan_items: PlanItem[] },
): { findings: DentalFinding[]; plan_items: PlanItem[] } {
  const storedIds = new Set(stored.plan_items.map((i) => i.id));
  return {
    findings: sanitizeFindings([...stored.findings, ...session.findings]),
    plan_items: [...stored.plan_items, ...session.plan_items.filter((i) => !storedIds.has(i.id))],
  };
}
